import React, { useState } from 'react'
import Datalist from 'components/Datalist'
import { TestLocations_testLocations, useTestLocations, useEditTestLocation } from '@modmd/data'
import Button from 'components/Button'
import Icon from 'components/Icon'
import { LocationDatalist } from 'sharedComponents/Locations'
import { Box } from 'components/Layout'
import { ConfirmationDialog, Dialog } from 'components/Dialog'
import { Text } from 'components/Typography'
import Card from 'components/Card'
import { Switch } from 'components/Switch'
import { CreateLocation } from './CreateLocation'

const Locations = () => {
  const [showDeleted, setShowDeleted] = useState(false)
  const { data: testLocations, loading: isTestLocationsLoading, refetch } = useTestLocations({
    showDeleted,
  })
  const [deleteLocation] = useEditTestLocation()
  const [confirmDeletePrice, setConfirmDeletePrice] = useState(false)
  const [isAddingDialog, setIsAddingDialog] = useState(false)
  const [selectedLocation, setSelectedLocation] = useState<TestLocations_testLocations | undefined>(
    undefined
  )

  const onDelete = async () => {
    await deleteLocation({
      variables: {
        inputData: {
          id: selectedLocation?.id || '',
          deleted: !selectedLocation?.deleted,
        },
      },
    })
    setConfirmDeletePrice(false)
    setSelectedLocation(undefined)
  }

  const handleDelete = (
    item: TestLocations_testLocations,
    e: React.MouseEvent<HTMLElement, MouseEvent>
  ) => {
    e.stopPropagation()
    setConfirmDeletePrice(true)
    setSelectedLocation(item)
  }

  const handleEdit = (
    item: TestLocations_testLocations,
    e: React.MouseEvent<HTMLElement, MouseEvent>
  ) => {
    e.stopPropagation()
    setIsAddingDialog(true)
    setSelectedLocation(item)
  }

  const onDismiss = () => {
    setIsAddingDialog(false)
    setSelectedLocation(undefined)
    if (!selectedLocation) void refetch()
  }

  return (
    <>
      <LocationDatalist
        inputData={testLocations?.testLocations ?? []}
        isLoading={isTestLocationsLoading}
        tableHeader={
          <Datalist.Title
            rightContent={
              <>
                <Box display="flex" flexDirection="column" alignItems="center">
                  <Text fontSize="s">Show deleted</Text>
                  <Switch
                    size="small"
                    isChecked={showDeleted}
                    onChange={() => setShowDeleted(!showDeleted)}
                  />
                </Box>
                <Button
                  colorVariant="primary"
                  onClick={() => {
                    setIsAddingDialog(true)
                  }}
                >
                  <Icon.Plus size="L" /> Add Location
                </Button>
              </>
            }
          >
            Locations
          </Datalist.Title>
        }
        requestDebounceInMs={250}
        pageLength={0}
        handleDelete={handleDelete}
        handleEdit={handleEdit}
      />
      <Dialog
        isOpen={!!isAddingDialog}
        onDismiss={() => onDismiss}
        variant="center"
        maxWidth="545px"
      >
        <Card.Title
          onDismiss={() => {
            setIsAddingDialog(false)
            setSelectedLocation(undefined)
          }}
        >
          {`${selectedLocation ? 'Edit' : 'Add'} new location`}
        </Card.Title>
        <Card.Content>
          <CreateLocation data={selectedLocation} onDismiss={onDismiss} />
        </Card.Content>
      </Dialog>
      <ConfirmationDialog
        title={`${selectedLocation?.deleted ? 'Activate' : 'Delete'} location`}
        isOpen={confirmDeletePrice}
        onDismiss={() => {
          setConfirmDeletePrice(false)
          setSelectedLocation(undefined)
        }}
        actions={
          <>
            <Button
              onClick={() => {
                setConfirmDeletePrice(false)
                setSelectedLocation(undefined)
              }}
              colorVariant="secondary"
            >
              Cancel
            </Button>
            <Button onClick={onDelete} isFetching={false} disabled={false}>
              {selectedLocation?.deleted ? 'Activate' : 'Delete'}
            </Button>
          </>
        }
      >
        <Box display="flex" flexDirection="column">
          <Text>
            Are you sure you want to {selectedLocation?.deleted ? 'activate ' : 'remove '}
            <b>{selectedLocation?.name}</b>
          </Text>
        </Box>
      </ConfirmationDialog>
    </>
  )
}

export { Locations }
